import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState, useCallback, ChangeEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    getManifestById,
    destroyManifest,
    generateManifest,
    getNewManifestGroupsByParcelIds,
    generatePDF,
} from 'src/api/shippingPortal';
import NewManifestGrid from './NewManifestGrid';
import { openPDFInNewTab } from 'src/util/openPdf';
import './EditManifestView.scss';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ExpandedState } from '@tanstack/react-table';
import { SelectedRows, MappedManifest, MappedParcel } from './types';
import { formatEndpointAddressHtml } from 'src/util/formatters';
import { ShippingPortalManifest, ShippingPortalParcel, DateFormats } from '@lanterds/types';
import { getCapitalized, getShipmentTypeFromContext } from 'src/util/helpers';
import Header from 'src/components/common/Header/Header';
import { PAGE_INFO, SELECTED_SHIPPING_PORTAL_TYPE_LOCAL_STORAGE_KEY } from 'src/util/constants';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useSnackbar, SnackbarKey } from 'notistack';

export default function EditManifestView() {
    const fmtpNavigate = useFmtpNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const idsFromMainPage = location?.state ? location.state['ids'] ?? null : null;
    const isNewManifest = id === 'new';
    const manifestShippingType = getShipmentTypeFromContext(SELECTED_SHIPPING_PORTAL_TYPE_LOCAL_STORAGE_KEY, 'type');

    const [manifestInfo, setManifestInfo] = useState<ShippingPortalManifest>({} as ShippingPortalManifest);
    const [mappedLabels, setMappedLabels] = useState<MappedManifest[]>([]);
    const [parcels, setParcels] = useState<ShippingPortalParcel[]>([]);
    const [departureDate, setDepartureDate] = useState<Dayjs | null>(dayjs());
    const [notes, setNotes] = useState<string>('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [isGeneratingManifest, setIsGeneratingManifest] = useState(false);
    const [isPrintingManifest, setIsPrintingManifest] = useState(false);
    const [isPrintingLabels, setIsPrintingLabels] = useState(false);
    const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
    const [destroyDialogOpen, setDestroyDialogOpen] = useState(false);

    useEffect(() => {
        if (id && !isNewManifest) {
            getManifestById(id).then((manifest: ShippingPortalManifest) => {
                setManifestInfo(manifest);
                setParcels(manifest.parcels ?? []);
                setDepartureDate(dayjs(manifest.departureDate));
                setNotes(manifest.note);
                setIsFetching(false);
            });
        } else if (isNewManifest && idsFromMainPage?.length) {
            getNewManifestGroupsByParcelIds(idsFromMainPage).then((manifests: ShippingPortalManifest[]) => {
                const groupedParcels = createManifestGroupings(manifests);
                setMappedLabels(groupedParcels);
                createInitialSelections(groupedParcels);
                setInitialExpandedRows(groupedParcels);
                setIsFetching(false);
            });
        } else {
            setIsFetching(false);
        }
    }, [id]);

    useEffect(() => {
        const newMappedLabels = mappedLabels.map((manifest, mIndex) => {
            const updated =
                manifest.subRows?.map((subRow, pIndex) => ({
                    ...subRow,
                    shouldBeChecked: !!selectedRows[`${mIndex}.${pIndex}`],
                })) || [];
            return { ...manifest, subRows: updated };
        });
        setMappedLabels(newMappedLabels);
    }, [selectedRows]);

    const onClickGenerateManifest = async () => {
        if (!departureDate) {
            enqueueSnackbar('Please enter an Expected Departure Date before generating manifests.', { variant: 'warning' });
            return;
        }
        const iso = dayjs(departureDate).format(DateFormats.IsoDate);
        if (iso === 'Invalid Date') {
            enqueueSnackbar('Expected Departure Date must be a valid date.', { variant: 'warning' });
            return;
        }

        const labelsToAdd = mappedLabels
            .flatMap((m) => m.subRows ?? [])
            .filter((p) => p.shouldBeChecked)
            .map((p) => p.id);

        if (!labelsToAdd.length) {
            enqueueSnackbar('Please select at least one parcel.', { variant: 'warning' });
            return;
        }

        setGenerateDialogOpen(true);

        setIsGeneratingManifest(true);
        try {
            const response = await generateManifest(labelsToAdd, iso, notes);
            if (response?.data?.data?.length) {
                enqueueSnackbar('Manifests generated successfully.', { variant: 'success' });
                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${manifestShippingType}`);
            } else {
                throw new Error();
            }
        } catch {
            enqueueSnackbar('Failed to generate manifest. Please try again.', { variant: 'error' });
        } finally {
            setIsGeneratingManifest(false);
        }
    };

    const onClickPrintManifest = async () => {
        if (!id) return;
        const key: SnackbarKey = enqueueSnackbar('Generating manifest PDF…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (k) => (
                <Button size="small" onClick={() => closeSnackbar(k)}>
                    Dismiss
                </Button>
            ),
        });
        setIsPrintingManifest(true);

        try {
            const response = await generatePDF([{ type: 'manifest', id, includeAllLabels: false }]);
            if (response.pdfBase64) {
                openPDFInNewTab(response.pdfBase64);
                enqueueSnackbar('Manifest PDF ready.', { variant: 'success' });
            } else {
                throw new Error('No PDF returned');
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to generate manifest PDF.', { variant: 'error' });
        } finally {
            setIsPrintingManifest(false);
            closeSnackbar(key);
        }
    };

    const onClickPrintLabels = async () => {
        if (!parcels?.length) return;
        const key: SnackbarKey = enqueueSnackbar('Generating labels PDF…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (k) => (
                <Button size="small" onClick={() => closeSnackbar(k)}>
                    Dismiss
                </Button>
            ),
        });
        setIsPrintingLabels(true);

        try {
            const response = await generatePDF(parcels.map((p) => ({ id: p.id, type: 'label' })));
            if (response.pdfBase64) {
                openPDFInNewTab(response.pdfBase64);
                enqueueSnackbar('Labels PDF ready.', { variant: 'success' });
            } else {
                throw new Error('No PDF returned');
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to generate labels PDF.', { variant: 'error' });
        } finally {
            setIsPrintingLabels(false);
            closeSnackbar(key);
        }
    };

    const onClickDestroy = () => {
        setDestroyDialogOpen(true);
    };

    const onClickCancel = () => {
        fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${manifestShippingType}`);
    };

    const enableRowSelection = () => true;

    const onDepartureDateChange = (value: Dayjs | null) => setDepartureDate(value);

    const onNotesChange = (e: ChangeEvent<HTMLInputElement>) => setNotes(e.target.value);

    const setInitialExpandedRows = (data: MappedManifest[]) => {
        const newExp: Record<string, boolean> = {};
        data.forEach((_, i) => (newExp[i] = true));
        setExpanded(newExp);
    };

    const createInitialSelections = (data: MappedManifest[]) => {
        const sel: SelectedRows = {};
        data.forEach(
            (manifest, manifestIndex) =>
                manifest.subRows?.forEach((parcel, parcelIndex) => {
                    if (parcel.shouldBeChecked) sel[`${manifestIndex}.${parcelIndex}`] = true;
                }),
        );
        setSelectedRows(sel);
    };

    const createManifestGroupings = (manifests: ShippingPortalManifest[]): MappedManifest[] => {
        const blank = '-';
        return manifests.map((manifest) => {
            const mappedParcels: MappedParcel[] = (manifest.parcels || []).map((parcel) => ({
                ...parcel,
                origin: blank,
                destination: blank,
                shouldBeChecked: idsFromMainPage.includes(parcel.id),
            }));
            return {
                ...manifest,
                parcelNumber: blank,
                reference: blank,
                subRows: mappedParcels,
            };
        });
    };

    const onExpandedChange = useCallback((row: () => ExpandedState) => {
        const key = Object.keys(row())[0];
        setExpanded((e) => ({ ...e, [key]: !e[key] }));
    }, []);

    const editManifestColumns = [
        { accessorKey: 'parcelNumber', header: 'Barcode', enableSorting: false },
        { accessorKey: 'labelSize', header: 'Label Size', enableSorting: false },
        { accessorKey: 'reference', header: 'Reference', enableSorting: false },
        {
            accessorKey: 'origin',
            header: 'Origin',
            meta: {
                innerHtml: ({ row }: CellContext<ShippingPortalParcel, unknown>) =>
                    formatEndpointAddressHtml(row.original.origin, true),
            },
        },
        {
            accessorKey: 'destination',
            header: 'Destination',
            meta: {
                innerHtml: ({ row }: CellContext<ShippingPortalParcel, unknown>) =>
                    formatEndpointAddressHtml(row.original.destination, true),
            },
        },
    ];

    const getHeaderText = () => {
        if (isNewManifest) {
            return `New ${getCapitalized(manifestShippingType)} Manifests`;
        }
        return manifestInfo.manifestNumber
            ? `Edit ${getCapitalized(manifestShippingType)} Manifest – ${manifestInfo.manifestNumber}`
            : '';
    };

    return (
        <Box id="edit-manifest-container" className="auto-fill-height">
            <Card id="edit-manifest-card" className="auto-fill-height">
                <Grid container spacing={2} className="form-container">
                    <Grid xs={12}>
                        <Header text={getHeaderText()} />
                    </Grid>

                    <Grid xs={12} container alignItems="flex-end" spacing={2}>
                        <Grid sm={2} xs={12}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        desktopModeMediaQuery="@media (min-width: 400px)"
                                        name="expectedDepartureDate"
                                        value={departureDate}
                                        disabled={!isNewManifest}
                                        onChange={onDepartureDateChange}
                                        slotProps={{
                                            field: { clearable: true },
                                            actionBar: { actions: ['clear', 'today'], sx: { justifyContent: 'space-between' } },
                                            textField: {
                                                id: 'expectedDepartureDate',
                                                variant: 'outlined',
                                                placeholder: 'MM/DD/YYYY',
                                                label: 'Expected Departure Date',
                                                InputProps: { fullWidth: true },
                                                required: true,
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid sm={2} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    type="text"
                                    name="notesToConsignee"
                                    variant="outlined"
                                    multiline
                                    maxRows={2}
                                    minRows={1}
                                    label="Notes to Consignee"
                                    value={notes}
                                    disabled={!isNewManifest}
                                    onChange={onNotesChange}
                                    fullWidth
                                />
                            </FormControl>
                        </Grid>

                        {isNewManifest && (
                            <Grid>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={onClickGenerateManifest}
                                    disabled={isGeneratingManifest}
                                >
                                    {isGeneratingManifest ? 'Generating Manifests…' : 'Generate Manifests'}
                                </Button>
                            </Grid>
                        )}
                        {!isNewManifest && (
                            <>
                                <Grid>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={onClickPrintManifest}
                                        disabled={isPrintingManifest}
                                    >
                                        {isPrintingManifest ? 'Generating…' : 'Print Manifest'}
                                    </Button>
                                </Grid>
                                <Grid>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={onClickPrintLabels}
                                        disabled={isPrintingLabels}
                                    >
                                        {isPrintingLabels ? 'Generating…' : 'Print Labels'}
                                    </Button>
                                </Grid>
                                <Grid>
                                    <Button fullWidth variant="contained" onClick={onClickDestroy}>
                                        Void Manifest
                                    </Button>
                                </Grid>
                            </>
                        )}
                        <Grid>
                            <Button fullWidth variant="contained" onClick={onClickCancel}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <CardContent className="table-card auto-fill-height">
                    {!isNewManifest ? (
                        <FmtpTable
                            columns={editManifestColumns}
                            data={parcels}
                            enableExpanding
                            enableRowSelection={enableRowSelection}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            onExpandedChange={onExpandedChange}
                            expanded={expanded}
                            isFetching={isFetching}
                        />
                    ) : (
                        <NewManifestGrid
                            data={mappedLabels}
                            enableExpanding
                            enableRowSelection={enableRowSelection}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            onExpandedChange={onExpandedChange}
                            expanded={expanded}
                            isFetching={isFetching}
                        />
                    )}
                </CardContent>
            </Card>
            <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)}>
                <DialogTitle>Generate Manifests?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to generate manifests with these labels and an Expected Departure Date of{' '}
                        {departureDate?.format(DateFormats.ShortDate)}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setGenerateDialogOpen(false);
                            setIsGeneratingManifest(true);
                            try {
                                const iso = departureDate!.format(DateFormats.IsoDate);
                                const labelsToAdd = mappedLabels
                                    .flatMap((m) => m.subRows ?? [])
                                    .filter((p) => p.shouldBeChecked)
                                    .map((p) => p.id);
                                const res = await generateManifest(labelsToAdd, iso, notes);
                                if (res?.data?.data?.length) {
                                    enqueueSnackbar('Manifests generated successfully.', { variant: 'success' });
                                    fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${manifestShippingType}`);
                                } else {
                                    throw new Error();
                                }
                            } catch {
                                enqueueSnackbar('Failed to generate manifest. Please try again.', { variant: 'error' });
                            } finally {
                                setIsGeneratingManifest(false);
                            }
                        }}
                        disabled={isGeneratingManifest}
                    >
                        {isGeneratingManifest ? 'Generating…' : 'Yes, generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={destroyDialogOpen} onClose={() => setDestroyDialogOpen(false)}>
                <DialogTitle>Void Manifest?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to cancel this manifest? This cannot be undone.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDestroyDialogOpen(false)}>No</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setDestroyDialogOpen(false);
                            if (!id) return;
                            try {
                                await destroyManifest(id);
                                enqueueSnackbar('Manifest voided.', { variant: 'info' });
                                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}?type=${manifestShippingType}`);
                            } catch {
                                enqueueSnackbar('Failed to void manifest.', { variant: 'error' });
                            }
                        }}
                    >
                        Yes, void
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
